import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";
import { ExternalLink, X } from "lucide-react";

interface Competitor {
  id: string;
  name: string;
  url?: string;
  key_locations?: string;
  funding_rounds?: string;
  key_investors?: string;
  genre?: string;
  crunchbase_link?: string;
  logo_url?: string;
  parent_company?: string;
  sites_count?: number;
  press_articles?: Array<{ title: string; url: string }>;
}

interface ProjectCompetitor {
  how_different: string;
  display_order: number;
  competitors: Competitor;
}

interface CompetitionWidgetProps {
  projectId: string;
}

export default function CompetitionWidget({
  projectId,
}: CompetitionWidgetProps) {
  const [competitors, setCompetitors] = useState<ProjectCompetitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompetitor, setSelectedCompetitor] =
    useState<ProjectCompetitor | null>(null);

  useEffect(() => {
    fetchCompetitors();
  }, [projectId]);

  const fetchCompetitors = async () => {
    try {
      setLoading(true);

      const { data, error: fetchError } = await supabase
        .from("project_competitors")
        .select(
          `
          how_different,
          display_order,
          competitors (
            id,
            name,
            url,
            key_locations,
            funding_rounds,
            key_investors,
            genre,
            crunchbase_link,
            logo_url,
            parent_company,
            sites_count,
            press_articles
          )
        `
        )
        .eq("project_id", projectId)
        .order("display_order", { ascending: true });

      if (fetchError) throw fetchError;

      const transformedData = (data || [])
        .map((item: any) => ({
          how_different: item.how_different,
          display_order: item.display_order,
          competitors: Array.isArray(item.competitors)
            ? item.competitors[0]
            : item.competitors,
        }))
        .filter((item: any) => item.competitors);

      setCompetitors(transformedData);
    } catch (err) {
      console.error("Error fetching competitors:", err);
      setError("Failed to load competitors");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card variant="bordered">
        <CardHeader color="purple">
          <h3 className="text-lg sm:text-xl font-bold text-black">
            Competition Overview
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Companies we are keeping a close eye on
          </p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
            Loading competitors...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || competitors.length === 0) {
    return null;
  }

  return (
    <>
      <Card variant="bordered">
        <CardHeader color="purple">
          <h3 className="text-lg sm:text-xl font-bold text-black">
            Competition Overview
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Companies we are keeping a close eye on
          </p>
        </CardHeader>
        <CardContent>
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {competitors.map((item) => {
              const competitor = item.competitors;
              return (
                <div
                  key={competitor.id}
                  onClick={() => setSelectedCompetitor(item)}
                  className="border-2 border-black rounded-lg p-4 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-base sm:text-lg font-bold text-black mb-2">
                        {competitor.name}
                      </h4>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600">
                        {competitor.key_locations && (
                          <span>📍 {competitor.key_locations}</span>
                        )}
                        {competitor.genre && <span>🏷️ {competitor.genre}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Modal Overlay */}
      {selectedCompetitor && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedCompetitor(null)}
        >
          <div
            className="bg-white rounded-lg border-4 border-black max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-purple-200 border-b-4 border-black p-4 sm:p-6">
              <div className="flex items-start gap-4">
                {selectedCompetitor.competitors.logo_url && (
                  <img
                    src={selectedCompetitor.competitors.logo_url}
                    alt={`${selectedCompetitor.competitors.name} logo`}
                    className="w-16 h-16 object-contain rounded border-2 border-black bg-white"
                  />
                )}
                <div className="flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold text-black mb-2">
                    {selectedCompetitor.competitors.name}
                  </h2>
                  {selectedCompetitor.competitors.parent_company && (
                    <div className="text-sm text-gray-700 mb-2">
                      Part of {selectedCompetitor.competitors.parent_company}
                    </div>
                  )}
                  {selectedCompetitor.competitors.url && (
                    <a
                      href={selectedCompetitor.competitors.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-black hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-4 h-4" />
                      Visit Website
                    </a>
                  )}
                </div>
                <button
                  onClick={() => setSelectedCompetitor(null)}
                  className="flex-shrink-0 p-2 hover:bg-black hover:bg-opacity-10 rounded transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6 text-black" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 space-y-6">
              {/* Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {selectedCompetitor.competitors.key_locations && (
                  <div>
                    <div className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Key Locations
                    </div>
                    <div className="text-sm sm:text-base text-black">
                      {selectedCompetitor.competitors.key_locations}
                    </div>
                  </div>
                )}
                {selectedCompetitor.competitors.genre && (
                  <div>
                    <div className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Genre
                    </div>
                    <div className="text-sm sm:text-base text-black">
                      {selectedCompetitor.competitors.genre}
                    </div>
                  </div>
                )}
                {selectedCompetitor.competitors.sites_count !== undefined && (
                  <div>
                    <div className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Number of Sites
                    </div>
                    <div className="text-sm sm:text-base text-black">
                      {selectedCompetitor.competitors.sites_count}
                    </div>
                  </div>
                )}
                {selectedCompetitor.competitors.funding_rounds && (
                  <div>
                    <div className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Funding Rounds
                    </div>
                    <div className="text-sm sm:text-base text-black">
                      {selectedCompetitor.competitors.funding_rounds}
                    </div>
                  </div>
                )}
                {selectedCompetitor.competitors.key_investors && (
                  <div className="sm:col-span-2">
                    <div className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Key Investors
                    </div>
                    <div className="text-sm sm:text-base text-black">
                      {selectedCompetitor.competitors.key_investors}
                    </div>
                  </div>
                )}
              </div>

              {/* External Links */}
              {(selectedCompetitor.competitors.crunchbase_link ||
                (Array.isArray(selectedCompetitor.competitors.press_articles) &&
                  selectedCompetitor.competitors.press_articles.length >
                    0)) && (
                <div className="pt-4 border-t-2 border-gray-200">
                  <div className="text-xs font-bold text-gray-600 uppercase mb-3">
                    External Resources
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedCompetitor.competitors.crunchbase_link && (
                      <a
                        href={selectedCompetitor.competitors.crunchbase_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white text-sm font-bold rounded-lg border-2 border-black hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Crunchbase
                      </a>
                    )}
                    {Array.isArray(
                      selectedCompetitor.competitors.press_articles
                    ) &&
                      selectedCompetitor.competitors.press_articles.map(
                        (article, index) => (
                          <a
                            key={index}
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white text-sm font-bold rounded-lg border-2 border-black hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                          >
                            <ExternalLink className="w-4 h-4" />
                            {article.title}
                          </a>
                        )
                      )}
                  </div>
                </div>
              )}

              {/* How We're Different */}
              <div className="pt-4 border-t-4 border-black">
                <div className="text-sm font-bold text-black mb-2 uppercase">
                  How We're Different
                </div>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  {selectedCompetitor.how_different}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
